import { useRef, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router";
import { ActionButton, BottomCTA, IconPersonFill, TextField, TopNavigation } from "@hamlak/ui";
import { Nickname } from "@hamlak/shared";
import { apiFetch, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { MePatchRequest, MePatchResponse } from "@/api/types";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { uploadAccountPhoto } from "./uploadAccountPhoto";
import "./me.css";

/**
 * 내 정보 편집 /me/edit(spec 7절). 사진은 고르면 즉시 저장(업로드 → PATCH /me {photoKey}), 닉네임은 저장 버튼으로 PATCH /me {displayName}.
 * 닉네임 형식은 shared Nickname 으로 실시간 검증, 중복·금지어는 서버 코드(NICKNAME_TAKEN·NICKNAME_FORBIDDEN)를 필드 에러로 매핑한다.
 */
export function MeEditPage() {
  const navigate = useNavigate();
  const account = useAuthStore((s) => s.account);
  const setAccount = useAuthStore((s) => s.setAccount);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const current = account?.displayName ?? "";
  const [typed, setTyped] = useState<string | null>(null); // null = 아직 손대지 않음 → 계정 값을 따라간다(마운트 때 account 가 비어 있어도 채워짐)
  const nickname = typed ?? current;
  const [serverError, setServerError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const parsed = Nickname.safeParse(nickname);
  const invalid = nickname.length > 0 && !parsed.success;
  const fieldError = serverError ?? (invalid ? t("nickname.invalid") : undefined);
  const canSave = parsed.success && parsed.data !== current && !saving;

  const back = () => {
    // 직접 진입(히스토리 없음)이면 /me 로
    if (typeof window !== "undefined" && (window.history.state as { idx?: number } | null)?.idx) navigate(-1);
    else navigate("/me", { replace: true });
  };

  const patch = (body: MePatchRequest) => apiFetch<MePatchResponse>(ep.me, { method: "PATCH", body });

  const saveNickname = async () => {
    if (!parsed.success) return;
    setSaving(true);
    try {
      const res = await patch({ displayName: parsed.data });
      setAccount({ displayName: res.user.displayName, photoUrl: res.user.photoUrl });
      back();
    } catch (e) {
      if (isApiError(e) && (e.code === "NICKNAME_TAKEN" || e.code === "NICKNAME_FORBIDDEN")) setServerError(t(`error.${e.code}`));
      else showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("error.generic"));
    } finally {
      setSaving(false);
    }
  };

  const changePhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // 같은 파일 재선택 허용
    if (!file) return;
    setPhotoBusy(true);
    setProgress(0);
    try {
      const { photoKey } = await uploadAccountPhoto(file, setProgress);
      const res = await patch({ photoKey });
      setAccount({ photoUrl: res.user.photoUrl });
    } catch (err) {
      // 디코드·업로드 실패 모두 기존 사진 유지
      const network = (isApiError(err) && err.code === "NETWORK") || (err instanceof Error && err.message === "UPLOAD_NETWORK");
      showSnackbar(network ? t("error.network") : t("meEdit.photo.error"));
    } finally {
      setPhotoBusy(false);
      setProgress(null);
    }
  };

  const deletePhoto = async () => {
    setPhotoBusy(true);
    try {
      const res = await patch({ photoKey: null });
      setAccount({ photoUrl: res.user.photoUrl });
    } catch (err) {
      showSnackbar(isApiError(err) && err.code === "NETWORK" ? t("error.network") : t("error.generic"));
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <main className="page auth" data-testid="me-edit-page">
      <TopNavigation title={t("page.meEdit")} right={null} onBack={back} />
      <div className="auth__form">
        <div className="meEdit__photo">
          {account?.photoUrl ? <img className="meEdit__avatar" src={account.photoUrl} alt={t("me.account.photoAlt")} /> : (
            // 사진 없으면 사람 아이콘 placeholder(AC0-232)
            <span className="meEdit__avatar meEdit__avatar--empty" data-testid="avatar-placeholder" aria-hidden>
              <IconPersonFill size={52} />
            </span>
          )}
          {progress !== null ? (
            <p className="meEdit__progress" role="status">
              {t("meEdit.photo.uploading", { percent: Math.round(progress * 100) })}
            </p>
          ) : null}
          <div className="meEdit__photoActions">
            <ActionButton variant="neutralWeak" size="small" disabled={photoBusy} onClick={() => fileRef.current?.click()}>
              {t("meEdit.photo.change")}
            </ActionButton>
            {account?.photoUrl ? (
              <ActionButton variant="neutralWeak" size="small" disabled={photoBusy} onClick={() => void deletePhoto()}>
                {t("meEdit.photo.delete")}
              </ActionButton>
            ) : null}
          </div>
          <input
            ref={fileRef}
            className="meEdit__file"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/*"
            aria-label={t("meEdit.photo.change")}
            data-testid="photo-input"
            onChange={(e) => void changePhoto(e)}
          />
        </div>
        <TextField
          label={t("meEdit.nickname")}
          value={nickname}
          onValueChange={(v) => {
            setTyped(v);
            setServerError(undefined);
          }}
          maxLength={16}
          {...(fieldError ? { error: fieldError } : {})}
          autoComplete="nickname"
        />
      </div>
      <BottomCTA primary={{ children: t("meEdit.save"), disabled: !canSave, loading: saving, onClick: () => void saveNickname() }} />
    </main>
  );
}
